import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue";
const _hoisted_1 = ["placeholder"];
const _hoisted_2 = {
    key: 0,
    class: "autocomplete-value__dropdown"
};
const _hoisted_3 = { class: "autocomplete-value__list" };
const _hoisted_4 = {
    key: 0,
    class: "spinner autocomplete-value__loading"
};
const _hoisted_5 = ["title", "onClick"];
const _hoisted_6 = { class: "autocomplete-value__label" };
const _hoisted_7 = {
    key: 2,
    class: "autocomplete-value__no-results"
};
const _hoisted_8 = {
    key: 3,
    class: "autocomplete-value__placeholder"
};
const _hoisted_9 = {
    key: 0,
    class: "autocomplete-value__selected-items"
};
const _hoisted_10 = ["onClick"];
const _hoisted_11 = { class: "autocomplete-value__btn-confirm" };
import IconBase from '@/assets/vue-icons/IconBase.vue';
import IconTrash from '@/assets/vue-icons/icons/Trash.vue';
import CheckBox from '@/components/_atoms/CheckBox.vue';
import LoadSpinner from '@/components/_atoms/LoadSpinner.vue';
import { useAudienceManager } from '@/features/AudienceManager/composables/useAudienceManager';
import { useClickOutside } from '@/features/AudienceManager/composables/useClickOutside';
import { fetchResultByTerm } from '@/features/AudienceManager/data';
import { formatLabels } from '@/features/AudienceManager/utils/format';
import i18n from '@/i18n';
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
const ITEMS_PER_PAGE = 25;
export default /*#__PURE__*/ _defineComponent({
    __name: 'Autocomplete',
    setup(__props) {
        const store = useStore();
        const groupIndex = inject('groupIndex');
        const isInclude = inject('isInclude');
        const groupType = inject('groupType');
        const { draftCondition, selectedCriteriaDetails, keepFooterLoadingDuringInteraction } = useAudienceManager(isInclude, groupIndex);
        const dropdownRef = ref(null);
        const searchText = ref('');
        const autocompleteResults = ref({});
        const isLoading = ref(false);
        const visibleItemsCount = ref(ITEMS_PER_PAGE);
        const scrollListRef = ref(null);
        const selectedKeys = ref(draftCondition.value?.value);
        const selectedItemsMap = ref(draftCondition.value?.referenceObjects || {});
        const { isDropdownOpen } = useClickOutside(dropdownRef, () => {
            setDropdownVisibility(false);
        });
        const hasResults = computed(() => Object.keys(autocompleteResults.value).length > 0);
        const isMultiSelect = computed(() => selectedCriteriaDetails.value?.multiple);
        const hasSelectedItems = computed(() => selectedKeys.value.length > 0);
        const isSearchValid = computed(() => searchText.value.trim().length > 0);
        const visibleResults = computed(() => {
            const allKeys = Object.keys(autocompleteResults.value);
            const visibleKeys = allKeys.slice(0, visibleItemsCount.value);
            const result = {};
            visibleKeys.forEach(key => {
                result[key] = autocompleteResults.value[key];
            });
            return result;
        });
        const hasMoreResults = computed(() => {
            const totalResultsCount = Object.keys(autocompleteResults.value).length;
            const visibleResultsCount = Object.keys(visibleResults.value).length;
            return totalResultsCount > visibleResultsCount;
        });
        const getPlaceholder = (type) => ({
            ORDER_STORE: i18n.global.t('audiences.store-placeholder'),
            LAST_STORE: i18n.global.t('audiences.store-placeholder'),
            AUDIENCE: i18n.global.t('audiences.audience-placeholder'),
            FOLLOWUP_CAMPAIGN: i18n.global.t('audiences.campaign-placeholder'),
        }[type] || '');
        const updateSearchText = () => {
            searchText.value = formatLabels(selectedItemsMap.value);
        };
        const initializeAutocomplete = () => {
            setDropdownVisibility(true);
            visibleItemsCount.value = ITEMS_PER_PAGE;
            if (selectedKeys.value.length) {
                const firstKey = selectedKeys.value[0];
                const firstWordToSearch = selectedItemsMap.value[firstKey].split(' ')[0];
                fetchSearchResults(firstWordToSearch);
            }
        };
        const debouncedFetch = debounce(async (searchTerm) => {
            const criteriaKey = selectedCriteriaDetails.value?.key;
            const response = await fetchResultByTerm(criteriaKey, searchTerm);
            updateAutocompleteResults(response);
            isLoading.value = false;
        }, 1500);
        const fetchSearchResults = (initSearchText = null) => {
            const searchTerm = initSearchText ?? searchText.value.trim();
            if (!isSearchValid.value) {
                isLoading.value = false;
                return;
            }
            keepFooterLoadingDuringInteraction();
            isLoading.value = true;
            debouncedFetch(searchTerm);
            setDropdownVisibility(true);
        };
        const updateAutocompleteResults = (response) => {
            autocompleteResults.value = response?.hasData ? { ...response.results } : {};
            visibleItemsCount.value = ITEMS_PER_PAGE;
        };
        const handleScroll = (event) => {
            const target = event.target;
            if (!target)
                return;
            const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
            if (scrollBottom < 30 && hasMoreResults.value) {
                visibleItemsCount.value += ITEMS_PER_PAGE;
            }
        };
        const selectItem = (key, label) => {
            isMultiSelect.value ? toggleItemSelection(key, label) : setSingleSelection(key, label);
            updateSearchText();
            updateDraftCondition();
        };
        const toggleItemSelection = (key, label) => {
            const isItemSelected = selectedKeys.value.includes(key);
            isItemSelected ? removeItem(key) : addSelectedItem(key, label);
        };
        const addSelectedItem = (key, label) => {
            selectedKeys.value.push(key);
            selectedItemsMap.value[key] = label;
        };
        const setSingleSelection = (key, label) => {
            selectedKeys.value = [key];
            selectedItemsMap.value = { [key]: label };
            searchText.value = label;
            closeDropdown();
        };
        const removeItem = (key) => {
            keepFooterLoadingDuringInteraction();
            const index = selectedKeys.value.indexOf(key);
            if (index !== -1) {
                selectedKeys.value.splice(index, 1);
            }
            delete selectedItemsMap.value[key];
        };
        const updateDraftCondition = () => {
            store.dispatch('audienceManager/updateDraftCondition', {
                groupType,
                groupIndex,
                data: {
                    value: [...selectedKeys.value],
                    referenceObjects: { ...selectedItemsMap.value },
                },
            });
        };
        const setDropdownVisibility = (isVisible) => {
            if (!selectedCriteriaDetails.value?.autocomplete)
                return;
            isDropdownOpen.value = isVisible;
        };
        const confirmSelection = () => closeDropdown();
        const closeDropdown = () => setTimeout(() => setDropdownVisibility(false), 200);
        watch(draftCondition, newValue => {
            selectedKeys.value = newValue?.value || [];
            selectedItemsMap.value = newValue?.referenceObjects || {};
            updateSearchText();
        }, { deep: true, immediate: true });
        onMounted(() => {
            scrollListRef.value = dropdownRef.value?.querySelector('.autocomplete-value__scroll');
        });
        onBeforeUnmount(() => {
            debouncedFetch.cancel();
            if (scrollListRef.value) {
                scrollListRef.value = null;
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "dropdownRef",
                ref: dropdownRef,
                class: _normalizeClass(['autocomplete-value', { 'autocomplete-value--active': _unref(isDropdownOpen) }])
            }, [
                _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((searchText).value = $event)),
                    type: "text",
                    class: "autocomplete-value__input",
                    onInput: _cache[1] || (_cache[1] = ($event) => (fetchSearchResults())),
                    onClick: initializeAutocomplete,
                    onFocus: initializeAutocomplete,
                    placeholder: getPlaceholder(_unref(selectedCriteriaDetails).key)
                }, null, 40, _hoisted_1), [
                    [_vModelText, searchText.value]
                ]),
                (_unref(isDropdownOpen))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("div", {
                                ref_key: "scrollListRef",
                                ref: scrollListRef,
                                class: _normalizeClass(['autocomplete-value__scroll customScrollBar', { 'autocomplete-value__scroll--loading': isLoading.value }]),
                                onScroll: handleScroll
                            }, [
                                (isLoading.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                        _createVNode(LoadSpinner, {
                                            loading: isLoading.value,
                                            height: "30",
                                            id: "autocomplete-loader",
                                            color: "white"
                                        }, null, 8, ["loading"])
                                    ]))
                                    : (hasResults.value)
                                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(visibleResults.value, (label, key) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                                key: key,
                                                title: label,
                                                class: "autocomplete-value__item",
                                                onClick: ($event) => (selectItem(key, label))
                                            }, [
                                                (isMultiSelect.value)
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        _createVNode(CheckBox, {
                                                            class: "autocomplete-value__checkbox",
                                                            checked: selectedKeys.value.includes(key)
                                                        }, null, 8, ["checked"]),
                                                        _createElementVNode("span", _hoisted_6, _toDisplayString(label), 1)
                                                    ], 64))
                                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                        _createTextVNode(_toDisplayString(label), 1)
                                                    ], 64))
                                            ], 8, _hoisted_5));
                                        }), 128))
                                        : (!hasResults.value && searchText.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.$t('audiences.no-results-data')), 1))
                                            : (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_ctx.$t('audiences.type-your-search')), 1))
                            ], 34),
                            (hasSelectedItems.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                    _createElementVNode("span", null, _toDisplayString(_ctx.$t('audiences.selected')), 1),
                                    _createElementVNode("div", {
                                        class: _normalizeClass(["autocomplete-value__scroll customScrollBar", {
                                                'autocomplete-value__scroll--loading': isLoading.value,
                                            }])
                                    }, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedItemsMap.value, (label, key) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                                key: key,
                                                class: "autocomplete-value__selected",
                                                onClick: ($event) => (removeItem(key))
                                            }, [
                                                _createElementVNode("span", null, _toDisplayString(label), 1),
                                                _createVNode(IconBase, {
                                                    color: "#EC3942",
                                                    class: "icon trash-icon"
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(IconTrash)
                                                    ]),
                                                    _: 1
                                                })
                                            ], 8, _hoisted_10));
                                        }), 128))
                                    ], 2),
                                    _createElementVNode("div", _hoisted_11, [
                                        _createElementVNode("button", {
                                            class: "btn",
                                            onClick: confirmSelection
                                        }, _toDisplayString(_ctx.$t('audiences.select')), 1)
                                    ])
                                ]))
                                : _createCommentVNode("", true)
                        ])
                    ]))
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
